import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import CarCard from "@/components/common/CarCard";
import { SEO } from "@/components/common/SEO";

const BODY = ["SUV", "Sedan", "Hatchback", "MPV"];
const FUEL = ["Petrol", "Diesel", "EV", "Hybrid", "CNG"];

export default function Cars() {
  const [params, setParams] = useSearchParams();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  const brand = params.get("brand") || "";
  const body = params.get("body_type") || "";
  const fuel = params.get("fuel") || "";
  const q = params.get("q") || "";
  const price_max = params.get("price_max") || "";
  const price_min = params.get("price_min") || "";

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (brand) qs.set("brand", brand);
    if (body) qs.set("body_type", body);
    if (fuel) qs.set("fuel", fuel);
    if (q) qs.set("q", q);
    if (price_max) qs.set("price_max", price_max);
    if (price_min) qs.set("price_min", price_min);
    api.get(`/cars?${qs.toString()}`).then((r) => setCars(r.data)).finally(() => setLoading(false));
  }, [brand, body, fuel, q, price_max, price_min]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v && next.get(k) !== v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="cars-page">
      <SEO title="Cars in India" description="Browse popular new cars in India by brand, body type and fuel — with AI insights." />
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Catalogue</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2 tracking-tight">All cars</h1>
        </div>
        <div className="text-sm text-muted-foreground">{cars.length} results</div>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        <Chip label="All bodies" active={!body} onClick={() => setFilter("body_type", "")} testId="filter-body-all"/>
        {BODY.map((b) => <Chip key={b} label={b} active={body === b} onClick={() => setFilter("body_type", b)} testId={`filter-body-${b}`}/>)}
        <span className="w-2"/>
        <Chip label="Any fuel" active={!fuel} onClick={() => setFilter("fuel", "")} testId="filter-fuel-all"/>
        {FUEL.map((f) => <Chip key={f} label={f} active={fuel === f} onClick={() => setFilter("fuel", f)} testId={`filter-fuel-${f}`}/>)}
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-72 rounded-3xl shimmer"/>
          ))}
        </div>
      ) : cars.length === 0 ? (
        <div className="rounded-3xl border border-border p-16 text-center text-muted-foreground">No cars match these filters.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cars.map((c) => <CarCard key={c.slug} car={c}/>)}
        </div>
      )}
    </div>
  );
}

function Chip({ label, active, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`h-9 px-4 rounded-full border text-sm transition-colors ${
        active ? "bg-[#2563EB] text-white border-[#2563EB]" : "border-border hover:bg-secondary"
      }`}
    >{label}</button>
  );
}
